import { computeInrValue, InrCellValue, isFxLive, resolveLineFieldValue } from './AdditionalChargeColumnRegistry';
import {
  AcLineError,
  AdditionalChargeExchangeRateLine,
  AdditionalChargeLineDraftEdits,
  AdditionalChargeLineDto,
} from './AdditionalChargeGrids.types';

/**
 * Additional Vendor Charges — rate-engine display precedence (ISS-292248, design decision F1).
 *
 * This module is the ONLY place decision F1 lives: **wherever the engine's live `resolution`
 * produces a figure, the client must not recompute or re-derive it.** Precedence per accessor,
 * applied consistently:
 *
 *   1. engine `resolution` field, when present — displayed verbatim, nothing else consulted;
 *   2. else the existing client-side behaviour (a staged draft edit, or `computeInrValue`'s live
 *      preview while the line is DRAFT/REWORK);
 *   3. else the persisted column (frozen lines: `rate`, `quantity`, `baseAmount`, `serviceStartDate`).
 *
 * No renderer (`AdditionalChargeGridColumns.tsx` and friends) may re-implement this precedence —
 * they call these accessors instead. `computeInrValue` / `isFxLive` / `resolveLineFieldValue`
 * themselves are untouched (ISS-292248 Phase 1 contract) — this module only decides whether they
 * get called at all.
 */

/** Re-exported so callers of `resolveInrCell` don't need a second import from the registry. */
export type { InrCellValue };

/**
 * The Rate cell: `resolution.rate.rate` when the engine priced this line (QUOTE-sourced Incidental
 * lines), else the existing draft-edit-or-persisted lookup (`resolveLineFieldValue`) — which is how
 * a MANUAL-rate line's typed edit keeps winning over its persisted `rate`.
 */
export const resolveDisplayRate = (
  line: AdditionalChargeLineDto,
  draftEdits: AdditionalChargeLineDraftEdits
): string | null => {
  const engineRate = line.resolution?.rate?.rate;
  if (engineRate != null) {
    return engineRate;
  }
  // The engine attempted to price this line but failed (e.g. a PCD failure, EADDCHARGE010/011): the rate
  // half carries an `error` and a null `rate`. Falling through to the persisted `line.rate` here would
  // paint a stale-but-plausible number on a cell that is read-only for a BID line — the vendor can't
  // correct it, and nothing signals it is not the live figure. Signal "unavailable" (null) instead so the
  // renderer shows its empty state rather than a wrong number (ISS-292248 F5).
  if (line.resolution?.rate?.error != null) {
    return null;
  }
  return resolveLineFieldValue(line, draftEdits, 'rate');
};

/**
 * The Currency cell: `resolution.rate.currency` when the engine priced this line (the quote card's own
 * currency), else the existing draft-edit-or-persisted lookup. Pairs with `resolveDisplayRate` so the
 * money + currency shown for an engine-priced line always come from the SAME resolution — otherwise the
 * rate cell can show a resolved figure while the currency cell still reads the persisted/empty
 * `line.currency` (Codex review P2).
 */
export const resolveDisplayCurrency = (
  line: AdditionalChargeLineDto,
  draftEdits: AdditionalChargeLineDraftEdits
): string | null => {
  const engineCurrency = line.resolution?.rate?.currency;
  if (engineCurrency != null) {
    return engineCurrency;
  }
  return resolveLineFieldValue(line, draftEdits, 'currency');
};

/**
 * The Quantity cell: `resolution.quantity.quantity` — the rounded product of every dim's `final` —
 * when the engine resolved it, else the persisted `quantity` column. Quantity has no draft-edit path
 * of its own (dim edits are staged separately, per-dim, and re-resolved server-side — see Task 3).
 */
export const resolveDisplayQuantity = (line: AdditionalChargeLineDto): string | null => {
  const engineQuantity = line.resolution?.quantity?.quantity;
  if (engineQuantity != null) {
    return engineQuantity;
  }
  return typeof line.quantity === 'string' ? line.quantity : null;
};

/**
 * The PCD cell: a frozen line (`!isFxLive`) always keeps its persisted `serviceStartDate` — PCD is
 * frozen at submit and the engine no longer runs for it. Otherwise `resolution.pcd` wins, then the
 * per-container PCD (`pcdByContainer[containerNumber]`, for CONTAINER-level vendor types), then the
 * payload-level `pcd` (SHIPMENT-level vendor types).
 */
export const resolveDisplayPcd = (
  line: AdditionalChargeLineDto,
  payloadPcd: string | null,
  pcdByContainer: Record<string, string | null> | undefined
): string | null => {
  if (!isFxLive(line)) {
    return line.serviceStartDate;
  }
  if (line.resolution?.pcd != null) {
    return line.resolution.pcd;
  }
  if (
    line.containerNumber &&
    pcdByContainer &&
    Object.prototype.hasOwnProperty.call(pcdByContainer, line.containerNumber)
  ) {
    return pcdByContainer[line.containerNumber];
  }
  return payloadPcd;
};

/**
 * The INR cell (Amount (INR) / 3rd Party Invoice Value (INR)): the engine's `baseAmount` wins
 * verbatim when present — `computeInrValue` is not called at all in that case, which is what keeps
 * the two numbers from ever diverging. When the engine has nothing for this line (a MANUAL-rate
 * Incidental/Self-Reimbursement line, a Third-Party line — the engine only prices `INCIDENTAL` +
 * `rate_config.source === QUOTE` lines — or the line is frozen), this delegates unchanged to
 * `computeInrValue`, which already covers the frozen/live/third-party cases.
 */
export const resolveInrCell = (
  line: AdditionalChargeLineDto,
  draftEdits: AdditionalChargeLineDraftEdits,
  exchangeRates: Record<string, number>,
  kind: 'amount' | 'invoiceValue'
): InrCellValue => {
  const engineAmount = line.resolution?.baseAmount;
  // The engine's `baseAmount` is only the INR figure when its `baseCurrency` actually IS INR. The backend
  // is allowed to settle a claim in a non-INR approval currency (R7), and this column's header is literally
  // "Amount (INR)" — so a non-INR `baseAmount` must NOT be shown here verbatim, or the grid labels a
  // foreign figure as INR. Latent under today's all-INR seed, but real (ISS-292248 F3). When it is not INR
  // we fall through to the currency-aware `computeInrValue` path (or the F1 error guard below) rather than
  // mislabel it.
  if (engineAmount != null && line.resolution?.baseCurrency === 'INR') {
    return { value: engineAmount, rateStatus: 'ok', rateUnavailable: false };
  }
  // The engine produced no usable INR `baseAmount`, but the line carries resolution errors (a missing FX
  // leg EADDCHARGE007, partially-filled USER_INPUT quantity dims, a PCD failure, …). `computeInrValue`
  // would then source the rate/currency from the PERSISTED `line.rate`/`line.currency`, so the INR cell
  // would compute off the old/null persisted rate while the Rate cell shows the engine figure (or its own
  // unavailable state) — the two numbers diverge. Show "unavailable" instead of a computed number
  // (ISS-292248 F1).
  if (lineErrors(line).length > 0) {
    return { value: null, rateStatus: 'unavailable', rateUnavailable: true };
  }
  // The engine priced no `baseAmount` (a MANUAL-rate Incidental/Self line) but may still have resolved a
  // QUANTITY (its dim product) that differs from the persisted `line.quantity`. `computeInrValue`
  // multiplies the rate by `line.quantity`, so feed it the engine-resolved quantity instead — otherwise
  // the live INR preview reads rate x persisted-qty (e.g. 5x too small) until the backend freezes the
  // line. Everything else (rate/currency/draft edits, FX) stays exactly as computeInrValue derives it,
  // preserving the F1 precedence and the untouched Phase-1 computeInrValue contract. (Codex review P1)
  const resolvedQuantity = line.resolution?.quantity?.quantity;
  const lineForInr = resolvedQuantity != null ? { ...line, quantity: resolvedQuantity } : line;
  return computeInrValue(lineForInr, draftEdits, exchangeRates, kind);
};

/** The union of every resolution failure on this line: the engine's live `resolution.errors` while
 * DRAFT/REWORK, else whatever the wire-level `errors` union carried (e.g. on a frozen/legacy line). */
export const lineErrors = (line: AdditionalChargeLineDto): AcLineError[] =>
  line.resolution?.errors ?? line.errors ?? [];

/**
 * True when this line must not be included in a submit-for-approval batch: it carries any error, or
 * its quantity resolution is an open-ended dim that has never been pinned by a vendor override
 * (`resolution.quantity.openEndedUnpinned`) — a line the engine could not fully quantify yet.
 */
export const isLineBlockedFromSubmit = (line: AdditionalChargeLineDto): boolean => {
  if (lineErrors(line).length > 0) {
    return true;
  }
  return line.resolution?.quantity?.openEndedUnpinned === true;
};

/**
 * The FX rate a single line was (or would be) converted at, when the line carries its OWN per-line rate:
 * the engine's live `resolution.fxRate` while DRAFT/REWORK, else the frozen persisted `fxRate`. `null`
 * when the line has no per-line rate of its own (a MANUAL / not-yet-resolved / freshly-picked-currency
 * line) — the footer builder then falls back to the tab-level payload rate map for that currency.
 */
export const resolveDisplayFxRate = (line: AdditionalChargeLineDto): string | null => {
  if (isFxLive(line)) {
    return line.resolution?.fxRate ?? null;
  }
  return line.fxRate;
};

const roundFxRate = (rate: number): number => Math.round(rate * 1e6) / 1e6;

/**
 * The exchange-rate footer rows for one vendor tab (ISS-292248 F6/F7). Consolidates what used to be
 * `collectUsedCurrencies` + `buildExchangeRateLines`:
 *
 *  - the currency a line contributes is its DISPLAYED currency (`resolveDisplayCurrency`) — so an
 *    engine-priced line whose persisted `currency` is null/stale, or a freshly-picked draft currency,
 *    is footed under the currency actually shown in its cell, not the raw persisted one (F6);
 *  - a line that carries its OWN per-line rate (`resolveDisplayFxRate`) is footed at that rate and its
 *    own resolved PCD (`resolveDisplayPcd`), so CONTAINER-level tabs whose containers froze at different
 *    PCDs surface a distinct `(rate, pcd)` row each, instead of one flat payload rate hiding the
 *    difference (F7);
 *  - a line with no per-line rate falls back to the tab-level payload rate map, dated at the payload PCD —
 *    exactly the prior behaviour, so a not-yet-resolved / freshly-picked currency still converts and shows.
 *
 * Rows are deduped by the `(currency, rate, pcd)` triple, INR/blank currencies and a rate of exactly 1 are
 * skipped, and rates are rounded to the precision the backend freezes at (NUMERIC(18,6)). An empty result
 * ⇒ the tab renders no footer, unchanged.
 */
export const buildTabExchangeRateLines = (
  sections: Array<{ lines: AdditionalChargeLineDto[] }>,
  draftEdits: AdditionalChargeLineDraftEdits,
  exchangeRates: Record<string, number>,
  payloadPcd: string | null,
  pcdByContainer: Record<string, string | null> | undefined
): AdditionalChargeExchangeRateLine[] => {
  const seen = new Set<string>();
  const rows: AdditionalChargeExchangeRateLine[] = [];
  sections.forEach((section) => {
    section.lines.forEach((line) => {
      const currency = resolveDisplayCurrency(line, draftEdits);
      if (!currency || currency === 'INR') {
        return;
      }
      const perLineFx = resolveDisplayFxRate(line);
      let rate: number | null;
      let pcd: string | null;
      if (perLineFx != null && perLineFx.trim() !== '' && !Number.isNaN(Number(perLineFx))) {
        rate = Number(perLineFx);
        pcd = resolveDisplayPcd(line, payloadPcd, pcdByContainer);
      } else {
        const mapped = exchangeRates[`${currency}-INR`];
        rate = typeof mapped === 'number' && Number.isFinite(mapped) ? mapped : null;
        pcd = payloadPcd;
      }
      if (rate === null || rate === 1) {
        return;
      }
      const rounded = roundFxRate(rate);
      const key = `${currency}|${rounded}|${pcd ?? ''}`;
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      rows.push({ currency, rate: rounded, pcd });
    });
  });
  return rows.sort((a, b) =>
    a.currency === b.currency
      ? String(a.pcd ?? '').localeCompare(String(b.pcd ?? ''))
      : a.currency.localeCompare(b.currency)
  );
};
